import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const STORAGE_KEY = "aima_cookie_consent";
const CONSENT_VERSION = 1;

const COPY = {
    en: {
        title: "Your privacy matters",
        text:
            "AIMA uses essential technologies required for the website to function. With your permission, we may also use analytics and third-party technologies to understand performance and improve the experience.",
        accept: "Accept all",
        reject: "Reject non-essential",
        manage: "Manage preferences",
        preferencesTitle: "Cookie preferences",
        preferencesText:
            "Choose which optional technologies you allow. Necessary technologies are always active because the website cannot function correctly without them.",
        necessary: "Necessary",
        necessaryText: "Required for core website functionality, security and basic operation.",
        analytics: "Analytics",
        analyticsText: "Helps us understand website performance and usage.",
        marketing: "Marketing & third-party",
        marketingText: "May be used by integrated third-party services where applicable.",
        alwaysActive: "Always active",
        save: "Save preferences",
        cancel: "Cancel",
    },

    ro: {
        title: "Confidențialitatea ta contează",
        text:
            "AIMA folosește tehnologii esențiale necesare funcționării website-ului. Cu acordul tău, putem utiliza și tehnologii de analiză și servicii terțe pentru a înțelege performanța și a îmbunătăți experiența.",
        accept: "Acceptă toate",
        reject: "Respinge non-esențialele",
        manage: "Gestionează preferințele",
        preferencesTitle: "Preferințe cookie",
        preferencesText:
            "Alege ce tehnologii opționale permiți. Tehnologiile necesare sunt întotdeauna active deoarece website-ul nu poate funcționa corect fără ele.",
        necessary: "Necesare",
        necessaryText: "Necesare pentru funcționalitatea de bază, securitate și operarea website-ului.",
        analytics: "Analytics",
        analyticsText: "Ne ajută să înțelegem performanța și modul de utilizare al website-ului.",
        marketing: "Marketing & servicii terțe",
        marketingText: "Pot fi utilizate de servicii terțe integrate, acolo unde este cazul.",
        alwaysActive: "Mereu active",
        save: "Salvează preferințele",
        cancel: "Anulează",
    },

};

const getStoredConsent = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;

        const parsed = JSON.parse(raw);

        if (parsed.version !== CONSENT_VERSION) {
            return null;
        }

        return parsed;
    } catch (_) {
        return null;
    }
};

const CookieConsent = () => {
    const { i18n } = useTranslation();

    const lang = ["en", "ro"].includes(i18n.language)
        ? i18n.language
        : "en";

    const t = COPY[lang];

    const [visible, setVisible] = useState(false);
    const [preferencesOpen, setPreferencesOpen] = useState(false);

    const [analytics, setAnalytics] = useState(false);
    const [marketing, setMarketing] = useState(false);

    useEffect(() => {
        const stored = getStoredConsent();

        if (!stored) {
            setVisible(true);
            return;
        }

        setAnalytics(Boolean(stored.analytics));
        setMarketing(Boolean(stored.marketing));
    }, []);

    const saveConsent = ({ analyticsAllowed, marketingAllowed }) => {
        const consent = {
            version: CONSENT_VERSION,
            necessary: true,
            analytics: analyticsAllowed,
            marketing: marketingAllowed,
            timestamp: new Date().toISOString(),
        };

        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
        } catch (_) {
            // If storage is unavailable, keep the website usable.
        }

        window.dispatchEvent(
            new CustomEvent("aima:consent-updated", {
                detail: consent,
            })
        );

        setAnalytics(analyticsAllowed);
        setMarketing(marketingAllowed);
        setVisible(false);
        setPreferencesOpen(false);
    };

    const acceptAll = () => {
        saveConsent({
            analyticsAllowed: true,
            marketingAllowed: true,
        });
    };

    const rejectNonEssential = () => {
        saveConsent({
            analyticsAllowed: false,
            marketingAllowed: false,
        });
    };

    const savePreferences = () => {
        saveConsent({
            analyticsAllowed: analytics,
            marketingAllowed: marketing,
        });
    };

    useEffect(() => {
        const openPreferences = () => {
            const stored = getStoredConsent();

            if (stored) {
                setAnalytics(Boolean(stored.analytics));
                setMarketing(Boolean(stored.marketing));
            }

            setVisible(false);
            setPreferencesOpen(true);
        };

        window.addEventListener(
            "aima:open-cookie-settings",
            openPreferences
        );

        return () => {
            window.removeEventListener(
                "aima:open-cookie-settings",
                openPreferences
            );
        };
    }, []);

    return (
        <>
            {visible && (
                <div
                    className="fixed inset-x-0 bottom-0 z-[9999] p-4 md:p-6"
                    role="region"
                    aria-label="Cookie consent"
                >
                    <div className="mx-auto max-w-6xl border border-white/[0.12] bg-[#071015]/95 backdrop-blur-xl shadow-[0_-20px_60px_rgba(0,0,0,0.45)]">
                        <div className="p-5 md:p-7">
                            <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                                <div className="max-w-3xl">
                                    <div className="mb-2 text-lg font-semibold text-white md:text-xl">
                                        {t.title}
                                    </div>

                                    <p className="m-0 text-sm leading-6 text-white/65 md:text-[15px]">
                                        {t.text}
                                    </p>
                                </div>

                                <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap lg:justify-end">
                                    <button
                                        type="button"
                                        onClick={() => setPreferencesOpen(true)}
                                        className="min-h-[44px] border border-white/[0.14] px-4 py-2.5 text-sm font-medium text-white/80 transition hover:border-white/30 hover:text-white"
                                    >
                                        {t.manage}
                                    </button>

                                    <button
                                        type="button"
                                        onClick={rejectNonEssential}
                                        className="min-h-[44px] border border-white/[0.2] px-4 py-2.5 text-sm font-medium text-white transition hover:border-cyan/60 hover:text-cyan"
                                    >
                                        {t.reject}
                                    </button>

                                    <button
                                        type="button"
                                        onClick={acceptAll}
                                        className="min-h-[44px] border border-cyan/70 bg-cyan/[0.1] px-5 py-2.5 text-sm font-semibold text-cyan transition hover:bg-cyan/[0.16] hover:shadow-[0_0_24px_-6px_rgba(0,229,255,0.7)]"
                                    >
                                        {t.accept}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {preferencesOpen && (
                <div
                    className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="cookie-preferences-title"
                >
                    <div className="w-full max-w-2xl border border-white/[0.12] bg-[#071015] shadow-2xl">
                        <div className="border-b border-white/[0.08] p-5 md:p-7">
                            <h2
                                id="cookie-preferences-title"
                                className="m-0 text-xl font-semibold text-white md:text-2xl"
                            >
                                {t.preferencesTitle}
                            </h2>

                            <p className="mt-3 mb-0 text-sm leading-6 text-white/60">
                                {t.preferencesText}
                            </p>
                        </div>

                        <div className="divide-y divide-white/[0.08]">
                            <div className="flex gap-4 p-5 md:p-6">
                                <div className="min-w-0 flex-1">
                                    <div className="font-medium text-white">
                                        {t.necessary}
                                    </div>
                                    <div className="mt-1 text-sm leading-5 text-white/55">
                                        {t.necessaryText}
                                    </div>
                                </div>

                                <div className="shrink-0 text-xs font-semibold uppercase tracking-wider text-cyan">
                                    {t.alwaysActive}
                                </div>
                            </div>

                            <label className="flex cursor-pointer gap-4 p-5 md:p-6">
                                <div className="min-w-0 flex-1">
                                    <div className="font-medium text-white">
                                        {t.analytics}
                                    </div>
                                    <div className="mt-1 text-sm leading-5 text-white/55">
                                        {t.analyticsText}
                                    </div>
                                </div>

                                <input
                                    type="checkbox"
                                    checked={analytics}
                                    onChange={(e) =>
                                        setAnalytics(e.target.checked)
                                    }
                                    className="mt-1 h-5 w-5 accent-cyan"
                                />
                            </label>

                            <label className="flex cursor-pointer gap-4 p-5 md:p-6">
                                <div className="min-w-0 flex-1">
                                    <div className="font-medium text-white">
                                        {t.marketing}
                                    </div>
                                    <div className="mt-1 text-sm leading-5 text-white/55">
                                        {t.marketingText}
                                    </div>
                                </div>

                                <input
                                    type="checkbox"
                                    checked={marketing}
                                    onChange={(e) =>
                                        setMarketing(e.target.checked)
                                    }
                                    className="mt-1 h-5 w-5 accent-cyan"
                                />
                            </label>
                        </div>

                        <div className="flex flex-col-reverse gap-2 border-t border-white/[0.08] p-5 sm:flex-row sm:justify-end md:p-6">
                            <button
                                type="button"
                                onClick={() => {
                                    setPreferencesOpen(false);

                                    if (!getStoredConsent()) {
                                        setVisible(true);
                                    }
                                }}
                                className="min-h-[44px] border border-white/[0.14] px-5 py-2.5 text-sm font-medium text-white/70 transition hover:text-white"
                            >
                                {t.cancel}
                            </button>

                            <button
                                type="button"
                                onClick={savePreferences}
                                className="min-h-[44px] border border-cyan/70 bg-cyan/[0.1] px-5 py-2.5 text-sm font-semibold text-cyan transition hover:bg-cyan/[0.16]"
                            >
                                {t.save}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default CookieConsent;